/**
 * Extract the music artist's name from a long-form NCS attribution
 * string for compact display in Circles UI.
 *
 * Source attributions follow the NCS template:
 *
 *   "Title" by Author [NCS Release] - https://ncs.io/...
 *
 * with the artist field optionally containing a feature credit:
 *
 *   "On & On" by Cartoon (feat. Daniel Levi) [NCS Release] - https://ncs.io/onandon
 *
 * Pulse's play screen renders the full string (legal + courtesy).
 * Circles' UI iterated to a tighter "Music by Author" format that
 * focuses the credit on who actually made the song — the licence
 * tag and URL are noise once the player is mid-game.
 *
 * Anchored on the actual NCS terminator (`[NCS ...]` tag, the
 * ` - URL` separator, or end-of-string), NOT on a generic
 * `[(\-—` character class. The previous version cut at the first
 * `(` or `-`, which truncated `(feat. X)` features off Cartoon-style
 * credits and butchered hyphenated artist names like `T-Mass`.
 *
 * Falls back to the original string if the attribution doesn't match
 * the template (e.g. user-imported tracks with a custom credit
 * format), so we never silently strip a credit we can't shorten.
 */
export function extractMusicAuthor(attribution: string | undefined | null): string | null {
  if (!attribution) {
    return null;
  }
  // Match `... by ARTIST<TERMINATOR>` where ARTIST may contain any
  // chars including parens / hyphens (so `(feat. X)` and `T-Mass`
  // survive intact). Terminators in priority order:
  //   - `[NCS` opening bracket of the licence tag (handles both
  //     `[NCS Release]` and any future `[NCS ...]` variant)
  //   - en-dash `–` (U+2013) or em-dash `—` (U+2014) separator
  //   - ` - http(s)?:` URL separator (HYPHEN-MINUS only matches when
  //     directly followed by the link, so dashes inside artist names
  //     aren't false positives)
  //   - end-of-string for credits with no licence tag
  const match = attribution.match(/\bby\s+(.+?)\s*(?:\[NCS|–|—| - https?:|$)/i);
  if (!match) {
    return attribution.trim() || null;
  }
  const author = match[1].trim().replace(/[,;]+$/, '').trim();
  return author.length > 0 ? author : attribution.trim() || null;
}

/**
 * Compact credit string for Circles UI: "Music by Cartoon" rather
 * than the full NCS template. Empty / null inputs return null so
 * callers can render conditionally.
 */
export function formatMusicCredit(attribution: string | undefined | null): string | null {
  const author = extractMusicAuthor(attribution);
  if (!author) {
    return null;
  }
  return `Music by ${author}`;
}

/**
 * Compact credit string for the osu! mapper that authored the
 * Circles chart. Charts imported from .osz files carry the original
 * mapper's username on `chart.mapper`; synthesised legacy charts
 * have no mapper and return `null`.
 *
 * Rendered alongside `formatMusicCredit` on the Circles play screen
 * so credit reads as e.g. "Music by Cartoon · Beatmap by Sylas".
 */
export function formatMapperCredit(mapper: string | undefined | null): string | null {
  if (!mapper) {
    return null;
  }
  const trimmed = mapper.trim();
  if (trimmed.length === 0) {
    return null;
  }
  return `Beatmap by ${trimmed}`;
}

/**
 * Combined credit line for Circles UI: "Music by X · Beatmap by Y"
 * (or just one half if the other is missing). Returns `null` when
 * neither credit is available so callers can render conditionally
 * without their own join logic.
 *
 * Both header (`CirclesPlayPage`) and start-overlay use the same
 * combined string, so centralising the formatting keeps the two
 * surfaces in lockstep.
 */
export function formatCirclesCredits(
  attribution: string | undefined | null,
  mapper: string | undefined | null,
): string | null {
  const parts = [
    formatMusicCredit(attribution),
    formatMapperCredit(mapper),
  ].filter((s): s is string => s !== null);
  if (parts.length === 0) {
    return null;
  }
  return parts.join(' \u00b7 ');
}
